/**
 * Fachliche Konstanten der anbieterunabhängigen CTI-Anbindung und der
 * KI-Audio-Pipeline. Die Werte sind bewusst anbieterneutral gehalten; die
 * providerspezifischen Feinheiten liegen im jeweiligen Client der API.
 */
export const TELEPHONY_PROVIDERS = ['RINGOVER', 'SIPGATE', 'TWILIO'];
/**
 * Verarbeitungsstrategie der Aufzeichnung. `AUTO_DETECT` entscheidet anhand der
 * tatsächlichen Kanalzahl: Stereo wird kanalweise transkribiert (exakte
 * Sprecherzuordnung), Mono über Diarisierung getrennt.
 */
export const AUDIO_CHANNEL_MODES = [
    'AUTO_DETECT',
    'STEREO_SPLIT_FASTER_WHISPER',
    'MONO_DIARIZATION_WHISPERX',
    'SINGLE_CHANNEL_PLAIN',
];
/** Modi, die eine Pipeline tatsächlich ausführen kann; `AUTO_DETECT` ist nur eine Vorgabe. */
export const RESOLVED_AUDIO_CHANNEL_MODES = [
    'STEREO_SPLIT_FASTER_WHISPER',
    'MONO_DIARIZATION_WHISPERX',
    'SINGLE_CHANNEL_PLAIN',
];
export const COMMUNICATION_DIRECTIONS = ['INBOUND', 'OUTBOUND'];
export const CALL_STATUSES = ['RINGING', 'ANSWERED', 'MISSED', 'VOICEMAIL', 'FAILED'];
export const AI_PROCESSING_STATUSES = [
    'PENDING',
    'DOWNLOADING_AUDIO',
    'TRANSCRIBING',
    'SUMMARIZING',
    'COMPLETED',
    'FAILED',
    'DISABLED',
];
export const AI_SENTIMENTS = ['POSITIVE', 'NEUTRAL', 'NEGATIVE', 'DISPUTED'];
/**
 * Sprecherkennung: Bei Stereo steht die Rolle fest (Kanal 0 = Mitarbeiter,
 * Kanal 1 = Kunde), bei Diarisierung liefert PyAnnote anonyme Kennungen.
 */
export const TRANSCRIPT_SPEAKER_PATTERN = /^(AGENT|CUSTOMER|UNKNOWN|SPEAKER_\d{2})$/u;
export const TRANSCRIPT_AGENT_SPEAKER = 'AGENT';
export const TRANSCRIPT_CUSTOMER_SPEAKER = 'CUSTOMER';
/** Stereo-Aufzeichnungen legen den Mitarbeiter auf den linken Kanal. */
export const STEREO_AGENT_CHANNEL = 0;
export const STEREO_CUSTOMER_CHANNEL = 1;
export const WHISPER_DEFAULT_API_URL = 'http://localhost:9000/v1/audio/transcriptions';
export const OLLAMA_DEFAULT_API_URL = 'http://localhost:11434';
export const OLLAMA_DEFAULT_MODEL = 'qwen2.5:3b-instruct';
/** Adresse, die im Ringover-Dashboard als Ziel der Ereigniszustellung hinterlegt wird. */
export const RINGOVER_WEBHOOK_PATH = '/api/v1/telephony/webhooks/ringover';
/** Header, unter dem Ringover die HMAC-SHA256-Signatur der Zustellung mitsendet. */
export const TELEPHONY_SIGNATURE_HEADER = 'x-telephony-signature';
/** Zeitgrenzen der externen Dienste; Transkription dauert deutlich länger als eine API-Abfrage. */
export const TELEPHONY_DOWNLOAD_TIMEOUT_MS = 60_000;
export const TELEPHONY_TRANSCRIPTION_TIMEOUT_MS = 600_000;
export const TELEPHONY_SUMMARY_TIMEOUT_MS = 180_000;
/** Obergrenze der herunterladbaren Aufzeichnung (rund 90 Minuten Stereo-WAV bei 16 kHz). */
export const TELEPHONY_MAX_RECORDING_BYTES = 200 * 1024 * 1024;
/** Obergrenze der Testdatei im Administrationsbereich. */
export const TELEPHONY_TEST_AUDIO_MAX_BYTES = 50 * 1024 * 1024;
export const TELEPHONY_LOG_PAGE_SIZE = 25;
export const TELEPHONY_SUMMARY_MAX_LENGTH = 5000;
export const TELEPHONY_NOTES_MAX_LENGTH = 10_000;
export const TELEPHONY_MAX_ACTION_ITEMS = 25;
export const TELEPHONY_MAX_TOPICS = 15;
/**
 * Vorgabe des Extraktions-Prompts. Der Platzhalter `{{transcript}}` wird durch
 * das formatierte Sprecher-Transkript ersetzt; fehlt er, wird das Transkript
 * angehängt. Das Modell antwortet ausschließlich als JSON-Objekt.
 */
export const TELEPHONY_DEFAULT_SYSTEM_PROMPT = `Du bist ein Assistent für die Nachbereitung von Telefonaten in einem Warenwirtschaftssystem.
Analysiere das folgende Gesprächsprotokoll zwischen einem Mitarbeiter (AGENT) und einem Kunden (CUSTOMER).

Antworte ausschließlich mit einem JSON-Objekt in genau dieser Struktur:
{
  "summary": "Sachliche Zusammenfassung in höchstens 6 Sätzen auf Deutsch.",
  "sentiment": "POSITIVE | NEUTRAL | NEGATIVE | DISPUTED",
  "actionItems": ["Konkrete Aufgabe für den Mitarbeiter", "..."],
  "topics": ["Schlagwort", "..."]
}

Regeln:
- Erfinde keine Inhalte; nutze nur das, was im Protokoll steht.
- "sentiment" beschreibt die Stimmung des Kunden. DISPUTED nur bei erkennbarem Streit oder Reklamation.
- "actionItems" enthält nur offene Aufgaben, keine bereits erledigten Punkte. Leeres Array, wenn nichts offen ist.
- "topics" enthält höchstens fünf kurze Schlagworte (Substantive).

Gesprächsprotokoll:
{{transcript}}`;
//# sourceMappingURL=telephony.js.map